import { Component, Element, h, Host, Prop, State } from '@stencil/core';
import { Location } from '~model/location';
import { get } from '~service/ajax-service';

@Component({
  tag: 'uvex-location-list',
  styleUrl: 'location-list.scss',
})
export class LocationList {
  @Element() el!: HTMLElement;

  @Prop() locationsUrl!: string;
  @State() activeLocation?: Location;

  activeFilters = {
    continent: '',
    country: '',
  };

  locations: Location[] = [];
  locations2: Location[] = [];

  groupedLocations: Location[] = [];

  filteredLocations: Location[] = [];

  gmap: HTMLUvexGoogleMapElement;

  markers: HTMLUvexGoogleMapMarkerElement[];

  async componentWillLoad() {
    await this.fetchLocations();
    this.groupLocations();
    this.filterLocations();
  }

  componentDidLoad() {
    this.gmap = this.el.querySelector('uvex-google-map') as HTMLUvexGoogleMapElement;
  }

  setContinentFilter(value) {
    this.activeFilters.continent = this.activeFilters.continent === value ? '' : value;
    this.activeFilters.country = '';
    this.filterLocations();
  }

  setCountryFilter(value) {
    this.activeFilters.country = this.activeFilters.country === value ? '' : value;
    this.filterLocations();
  }

  renderLocations() {
    if (this.groupedLocations.length === 0) {
      return '';
    }

    return (
      <uvex-accordion>
        {Object.keys(this.groupedLocations).map(continent => {
          return (
            <uvex-collapse isOpen={false} class={this.groupedLocations[continent]['open'] ? 'show' : ''}>
              <a class="collapse-headline" role="button" collapse-trigger onClick={_ => this.setContinentFilter(this.groupedLocations[continent]['name'])}>
                {this.groupedLocations[continent]['name']}
                <svg width="20" height="20">
                  <use href="#fa-chevron-down"></use>
                </svg>
              </a>
              <div class="collapse-content">
                {Object.keys(this.groupedLocations[continent]['countries']).map(country => {
                  return (
                    <uvex-accordion>
                      <uvex-collapse isOpen={false} class={this.groupedLocations[continent]['countries'][country]['open'] ? 'show' : ''}>
                        <a
                          class="collapse-headline"
                          role="button"
                          collapse-trigger
                          onClick={_ => this.setCountryFilter(this.groupedLocations[continent]['countries'][country]['name'])}
                        >
                          {this.groupedLocations[continent]['countries'][country]['name']}
                          <svg width="20" height="20">
                            <use href="#fa-chevron-down"></use>
                          </svg>
                        </a>
                        <div class="collapse-content">
                          {Object.keys(this.groupedLocations[continent]['countries'][country]['locations']).map(location => {
                            return (
                              <uvex-location-location
                                location={this.groupedLocations[continent]['countries'][country]['locations'][location]}
                                id={'dealerlocator-location-' + this.groupedLocations[continent]['countries'][country]['locations'][location]['id']}
                              />
                            );
                          })}
                        </div>
                      </uvex-collapse>
                    </uvex-accordion>
                  );
                })}
              </div>
            </uvex-collapse>
          );
        })}
      </uvex-accordion>
    );
  }

  renderPins() {
    // TODO: Use this.filteredLocations
    // As for now i cant get it to work to remove markers and just show the filtered ones
    // If we just use filteredLocations it doubles the markers
    // This is also broken in live environment. (Click on accordion continent should only show markers from this continent)
    return this.locations.map(location => {
      return (
        <uvex-location-pin
          location={location}
          active={this.activeLocation === location}
          onMapsClick={_ => (this.activeLocation = location)}
          onMapsInfoWindowOpen={_ => (this.activeLocation = location)}
          onMapsInfoWindowClose={_ => (this.activeLocation === location ? (this.activeLocation = null) : null)}
          id={'dealerlocator-pin-' + location.id}
          hidden={true}
        />
      );
    });
  }

  render() {
    this.selectLocation();

    return (
      <Host>
        <uvex-google-map cluster={true}>{this.renderPins()}</uvex-google-map>
        <div class="container">{this.renderLocations()}</div>
      </Host>
    );
  }

  private async fetchLocations() {
    this.activeLocation = null;
    this.locations = await get(this.locationsUrl);
  }

  private groupLocations() {
    let groupedLocations: any = this.locations;
    groupedLocations = this.groupBy(groupedLocations, 'continent');

    for (const [continentName, value] of Object.entries(groupedLocations)) {
      groupedLocations[continentName] = {
        name: continentName,
        open: false,
        countries: this.groupBy(value, 'country'),
      };

      for (const [countryName, locations] of Object.entries(groupedLocations[continentName].countries)) {
        groupedLocations[continentName].countries[countryName] = {
          name: countryName,
          open: false,
          locations: locations,
        };
      }
    }
    this.groupedLocations = groupedLocations;
  }

  private filterLocations() {
    let filteredLocations = this.locations;

    if (this.activeFilters.continent) {
      filteredLocations = filteredLocations.filter(location => {
        return location.continent === this.activeFilters.continent;
      });
    }

    if (this.activeFilters.country) {
      filteredLocations = filteredLocations.filter(location => location.country === this.activeFilters.country);
    }

    this.filteredLocations = filteredLocations;
  }

  private selectLocation() {
    this.resetOpenValuesInGroupedLocations();
    if (this.activeLocation) {
      this.activeFilters.continent = this.activeLocation.continent;
      this.activeFilters.country = this.activeLocation.country;
      this.groupedLocations[this.activeLocation.continent].open = true;
      this.groupedLocations[this.activeLocation.continent].countries[this.activeLocation.country].open = true;
    }
  }

  private resetOpenValuesInGroupedLocations() {
    Object.keys(this.groupedLocations).forEach(continent => {
      this.groupedLocations[continent].open = false;
      Object.keys(this.groupedLocations[continent].countries).forEach(country => {
        this.groupedLocations[continent].countries[country].open = false;
      });
    });
  }

  protected groupBy(value: any, groupBy: string) {
    value = value.reduce(function (previousValue, currentValue) {
      previousValue[currentValue[groupBy]] = previousValue[currentValue[groupBy]] || [];
      previousValue[currentValue[groupBy]].push(currentValue);
      return previousValue;
    }, Object.create(null));
    return value;
  }
}
